<script lang="ts">
	import Layout from "../components/Layout.svelte";
	import { getTranslate } from "../utils/getTranslate.js";
	import translations from "./ServiceSupport.translations.json";
	import thumbnail from "../assets/images/about/Spolecna.jpg";
	import { setContext } from "svelte";
	import type { HTMLAnchorAttributes } from "svelte/elements";
	import { thumbnailImageContextKey } from "../contexts/thumbnailImageContextKey.js";
	import { getTenant } from "../getTenant.js";
	import Icon from "../components/Icon.svelte";
	import Link from "../components/Link.svelte";
	import arrowRight from "../../../core/assets/icons/arrow-right.svg?raw";
	import iconNumbers from "../../../core/assets/icons/zero-one-numbers.svg?raw";
	import iconChatBubble from "../../../core/assets/icons/chat-bubble.svg?raw";
	import iconTools from "../../../core/assets/icons/tools.svg?raw";
	import { contactPathMap } from "../../../core/schema/paths/contactPathMap";
	import {
		integrationPathMap,
		serviceLifecyclePathMap,
	} from "../../../core/schema/paths/serviceSupportDetailPathMap.js";
	import { onMount } from "svelte";
	import { serviceSupportAnchorMap } from "../../../core/schema/anchors/serviceSupportAnchorMap.js";
	import { nbspify } from "../../../core/utils/nbspify";
	import { helpdeskUrlMap } from "../../../core/schema/urls/helpdeskUrlMap.js";
	import ContactForm from "../components/Contact/ContactForm.svelte";
	import HotlineGetInTouch from "../components/HotlineGetInTouch.svelte";
	import HeadingSection from "../components/HeadingSection.svelte";

	const translate = getTranslate(translations);
	const tenant = getTenant();

	setContext(thumbnailImageContextKey, thumbnail);

	interface Section {
		id: string;
		icon: string;
		label: keyof typeof translations;
		headline: keyof typeof translations;
		paragraphs: (keyof typeof translations)[];
		linkText: keyof typeof translations;
		linkUrl: string;
		linkTarget?: HTMLAnchorAttributes["target"];
	}

	const sections: Section[] = [
		{
			id: serviceSupportAnchorMap.integration[tenant.locale],
			icon: iconNumbers,
			label: "integrationLabel",
			headline: "integrationHeadline",
			paragraphs: ["integrationParagraph1", "integrationParagraph2"],
			linkText: "moreAboutIntegrationProcess",
			linkUrl: `/${integrationPathMap[tenant.locale]}`,
		},
		{
			id: serviceSupportAnchorMap.consultation[tenant.locale],
			icon: iconChatBubble,
			label: "consultationLabel",
			headline: "consultationHeadline",
			paragraphs: ["consultationParagraph1", "consultationParagraph2"],
			linkText: "contactUs",
			linkUrl: `/${contactPathMap[tenant.locale]}`,
		},
		{
			id: serviceSupportAnchorMap.service[tenant.locale],
			icon: iconTools,
			label: "serviceLabel",
			headline: "serviceHeadline",
			paragraphs: ["serviceParagraph"],
			linkText: "orderService",
			linkUrl: `/${serviceLifecyclePathMap[tenant.locale]}`,
		},
		{
			id: serviceSupportAnchorMap.financing[tenant.locale],
			icon: iconNumbers,
			label: "financingLabel",
			headline: "financingHeadline",
			paragraphs: ["financingParagraph1", "financingParagraph2"],
			linkText: "contactUs",
			linkUrl: `/${contactPathMap[tenant.locale]}`,
		},
		{
			id: serviceSupportAnchorMap.helpDesk[tenant.locale],
			icon: iconNumbers,
			label: "helpDeskLabel",
			headline: "helpDeskHeadline",
			paragraphs: ["helpDeskParagraph"],
			linkText: "SubmitServiceRequest",
			linkUrl: helpdeskUrlMap[tenant.locale],
			linkTarget: "_blank",
		},
	];

	let activeSection = sections[0]?.id ?? "";

	onMount(() => {
		const observers: IntersectionObserver[] = [];

		sections.forEach(({ id }) => {
			const el = document.getElementById(id);
			if (!el) {
				return;
			}

			const observer = new IntersectionObserver(
				([entry]) => {
					if (entry?.isIntersecting) {
						activeSection = id;
					}
				},
				{
					rootMargin: "-40% 0px -50% 0px",
					threshold: 0,
				},
			);

			observer.observe(el);
			observers.push(observer);
		});

		return (): void => {
			observers.forEach((o): void => {
				o.disconnect();
			});
		};
	});

	function scrollTo(id: string): void {
		document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
	}

	function handleSectionClick(id: string): () => void {
		return (): void => {
			scrollTo(id);
		};
	}
</script>

<Layout>
	<div class="container">
		<HeadingSection tag={translate("tag")} headline={translate("introductoryHeadline")}>
			<div class="lg:text-md/normal mb-5 tracking-[0.02em]">{translate("introductoryDesc1")}</div>
			<div class="lg:text-md/normal mb-5 tracking-[0.02em]">{translate("introductoryDesc2")}</div>
		</HeadingSection>
	</div>

	<section class="!mb-0">
		<div
			class="border-t-lttertiary grid grid-cols-1 border-y border-b-0 px-4 md:grid-cols-2 md:px-12 lg:px-16 xl:px-32"
		>
			<div class="hidden h-full md:-ml-12 md:block md:pl-6 lg:-ml-16 lg:pl-8 xl:-ml-32 xl:pl-16">
				<nav
					class="sticky top-0 flex h-screen flex-col justify-center py-12"
					aria-label={translate("sectionsNavigation")}
				>
					<ol class="grid list-none grid-cols-[auto_1fr] gap-x-3 [counter-reset:section]">
						{#each sections as section}
							<li
								class="font-bahnschrift col-span-2 grid grid-cols-subgrid items-baseline font-light transition-colors before:tabular-nums before:content-[counter(section)] before:[counter-increment:section] md:text-lg lg:text-[2.5rem] {activeSection ===
								section.id
									? 'text-ltprimary-300'
									: 'text-ltwhiteout-500'}"
							>
								<button on:click={handleSectionClick(section.id)} class="text-left font-medium uppercase">
									{nbspify(translate(section.label))}
								</button>
							</li>
						{/each}
					</ol>
				</nav>
			</div>

			<div class="border-x-lttertiary border">
				{#each sections as section (section.id)}
					<div
						id={section.id}
						class="border-b-lttertiary grid-cols-full grid scroll-mt-24 gap-16 border-b p-8 xl:grid-cols-4"
					>
						<div class="col-span-3">
							<Icon icon={section.icon} class="mb-16 size-20" />
							<h2 class="text-ltprimary mb-4 font-[350] uppercase tracking-[0.02em] md:text-[2.5rem]">
								{translate(section.headline)}
							</h2>
							{#each section.paragraphs as paragraph}
								<div class="text-base/[1.6] font-light tracking-normal">
									{translate(paragraph)}
								</div>
							{/each}
							<Link
								asButton
								variant="secondary"
								class="mt-16"
								icon={arrowRight}
								href={section.linkUrl}
								target={section.linkTarget}
							>
								{translate(section.linkText)}
							</Link>
						</div>
					</div>
				{/each}
			</div>
		</div>
	</section>

	<HotlineGetInTouch />

	<section class="!mb-0">
		<ContactForm containerClass="mx-4 md:px-8 lg:px-12 xl:px-28" />
	</section>
</Layout>
